import { useMemo } from "react";
import { DollarSign, TrendingUp, Flame, ArrowUpCircle, Hourglass } from "lucide-react";

import Panel from "../Panel";
import StatRow from "../StatRow";
import TrendChart from "../TrendChart";
import { AWAITING_CHECK_IN_NOTE, EARNINGS_NOTE, PROJECTION_NOTE } from "../InfoTip";
import { PageStack, TableScroll, Table, Th, Td, Tr, Empty, Badge } from "../ui";
import { PageProps } from "./types";
import {
  earningsByPlan,
  earningsSeries,
  formatCurrency,
  isTrialProduct,
  monthEarnings,
  planLabel,
} from "../../../utils/partnerMetrics";

const RevenuePage = ({ members, products, loading, projection, projectionLoading, capabilities }: PageProps) => {
  const earnings = useMemo(() => monthEarnings(members, products), [members, products]);
  const series = useMemo(() => earningsSeries(members, products), [members, products]);
  const plans = useMemo(() => earningsByPlan(members, products), [members, products]);

  const stats = [
    {
      label: "Earnings This Month",
      value: formatCurrency(earnings.totalEarnings),
      icon: DollarSign,
      hint: `${earnings.paidPeriods} checked-in membership${earnings.paidPeriods === 1 ? "" : "s"}`,
      info: EARNINGS_NOTE,
    },
    {
      label: "From Trials",
      value: formatCurrency(earnings.trialEarnings),
      icon: Flame,
      hint: "This month",
    },
    {
      label: "From Upgrades",
      value: formatCurrency(earnings.upgradeEarnings),
      icon: ArrowUpCircle,
      hint: "This month",
    },
    {
      label: "Awaiting Check-in",
      value: formatCurrency(earnings.awaitingCheckIn.earnings),
      icon: Hourglass,
      hint: `${earnings.awaitingCheckIn.count} membership${earnings.awaitingCheckIn.count === 1 ? "" : "s"} this month`,
      info: AWAITING_CHECK_IN_NOTE,
    },
  ];
  if (capabilities.canViewProjections) {
    stats.push({
      label: "Projected Next Month",
      value: projectionLoading ? "…" : projection ? formatCurrency(projection.value) : "—",
      icon: TrendingUp,
      hint: projection
        ? `${projection.memberCount} member${projection.memberCount === 1 ? "" : "s"} upgrading or renewing`
        : "Not available yet",
      info: PROJECTION_NOTE,
    });
  }

  return (
    <PageStack>
      <StatRow stats={stats} loading={loading} />
      <TrendChart
        title="Earnings by Month"
        data={series}
        loading={loading}
        color="#1f6f4a"
        valuePrefix="$"
        formatValue={formatCurrency}
        caption="this month so far"
        info={EARNINGS_NOTE}
        height={260}
      />
      <Panel
        title="Earnings by Plan"
        subtitle="Checked-in memberships that started or renewed this month"
        info={EARNINGS_NOTE}
      >
        {plans.length === 0 ? (
          <Empty>No checked-in memberships have started this month yet.</Empty>
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>Plan</Th>
                  <Th $align="right">Memberships</Th>
                  <Th $align="right">Member price</Th>
                  <Th $align="right">Your earnings</Th>
                </tr>
              </thead>
              <tbody>
                {plans.map((p) => (
                  <Tr key={p.productName}>
                    <Td>
                      <Badge $tone={isTrialProduct(p.productName) ? "gray" : "purple"}>
                        {planLabel(p.productName)}
                      </Badge>
                    </Td>
                    <Td data-label="Memberships" $align="right">{p.periods}</Td>
                    <Td data-label="Member price" $align="right">{formatCurrency(p.price)}</Td>
                    <Td data-label="Your earnings" $align="right">
                      <strong>{formatCurrency(p.studioEarnings)}</strong>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}
      </Panel>
    </PageStack>
  );
};

export default RevenuePage;
